import {createStyles, LoadingOverlay} from '@mantine/core';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {useSavingState} from 'benchling-apps/lib';
import BenchlingConfig from 'BenchlingConfig';
import {Audience, WebhookUrlRouting} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {Header3} from 'ui-library/Headers';
import SettingsField from 'ui-library/SettingsField';

import * as AppDefinitionActions from '../AppDefinitionActions';
import {UpdateBenchlingAppDefinitionArgs} from '../BenchlingAppDAO';
import {BenchlingAppCredentials} from './BenchlingAppCredentials';
import {AppDefinition} from './BenchlingAppsPage';
import {AppDefinitionWithOAuth} from './BenchlingGlobalAppPage';
import PublicKeySection from './PublicKeySection';
import WebhookSetupSection from './WebhookSetupSection';

export default function BenchlingAppOverview({
  appDefinition,
  refetchAppDefinition,
}: {
  appDefinition: AppDefinitionWithOAuth;
  refetchAppDefinition: () => void;
}): JSX.Element {
  // BenchlingAppOverview always renders for `AppDefinition` data, so updates
  // go through the new `/app-definitions/{id}` endpoint. The backend handles
  // syncing any installation tied to the definition.
  async function handleSubmitDefinition(newValue: UpdateBenchlingAppDefinitionArgs): Promise<void> {
    await AppDefinitionActions.updateAppDefinition(appDefinition.id, newValue);
    notifications.success(<>Successfully updated app</>);
  }

  return (
    <OverviewSection>
      <BenchlingAppGlobalInformation
        appDefinition={appDefinition}
        refetchAppDefinition={refetchAppDefinition}
        handleSubmitDefinition={handleSubmitDefinition}
      />
      <BenchlingAppCredentials
        appDefinition={appDefinition}
        refetchAppDefinition={refetchAppDefinition}
        handleSubmitDefinition={handleSubmitDefinition}
      />
      <BenchlingAppDistributionSettings appDefinition={appDefinition} />
    </OverviewSection>
  );
}

export function BenchlingAppGlobalInformation({
  appDefinition,
  refetchAppDefinition,
  handleSubmitDefinition,
}: {
  appDefinition: AppDefinitionWithOAuth;
  refetchAppDefinition: () => void;
  handleSubmitDefinition: (newValue: UpdateBenchlingAppDefinitionArgs) => void | Promise<void>;
}): JSX.Element {
  const {classes} = useStyles();
  const {isSaving, withSavingState} = useSavingState();

  let creatorName = '';
  if (appDefinition.creator != null) {
    creatorName = appDefinition.creator.name;
  }
  const webhookSetupSection = (
    <WebhookSetupSection
      webhookSetupIsAccessible={appDefinition.webhookSetupIsAccessible}
      webhookHeaders={appDefinition.webhookHeaders}
      webhookUrl={appDefinition.webhookUrl}
      // BenchlingAppOverview always renders an `AppDefinition`, which can
      // store a routing strategy, so the routing radio group is always
      // available here.
      webhookUrlRouting={appDefinition.webhookUrlRouting}
      showWebhookHeaders={BenchlingConfig.ENABLE_BENCHLING_APPS_WEBHOOK_HEADERS}
      onUpdateWebhookSetup={async (
        newWebhookUrl: string | null,
        newWebhookHeaders: Array<{key: string; value: string}>,
        newWebhookUrlRouting?: WebhookUrlRouting
      ) => {
        await AppDefinitionActions.updateAppDefinition(appDefinition.id, {
          webhookUrl: newWebhookUrl || null,
          webhookHeaders: newWebhookHeaders || null,
          ...(newWebhookUrlRouting !== undefined && {webhookUrlRouting: newWebhookUrlRouting}),
        });
      }}
    />
  );
  const publicKeySection = (
    <PublicKeySection
      value={appDefinition.publicKey}
      onChangePublicKey={(newPublicKey: string) => {
        withSavingState(() => handleSubmitDefinition({publicKey: newPublicKey || null}) as Promise<void>);
      }}
    />
  );
  return (
    <div className={classes.savingContainer}>
      <LoadingOverlay visible={isSaving} overlayOpacity={0.3} />
      <div>
        <Header3>Global information</Header3>
        <div className={classes.subheader}>
          Information about your app that is globally used for any version of this app.
        </div>

        <BenchlingAppSettingsRow>
          <SettingsField
            label="App ID"
            name="appId"
            value={appDefinition.id}
            isBeingCreated={false}
            onChange={() => {}}
            isWritable={false}
            dataTestAttrs={{component: 'BenchlingAppOverview', element: 'appDefinitionId'}}
          />
          <SettingsField
            label="Creator"
            name="creator"
            value={creatorName}
            isBeingCreated={false}
            onChange={() => {}}
            isWritable={false}
          />
          {publicKeySection}
        </BenchlingAppSettingsRow>
        <BenchlingAppSettingsRow>{webhookSetupSection}</BenchlingAppSettingsRow>
      </div>
    </div>
  );
}

export function BenchlingAppDistributionSettings({
  appDefinition,
}: {
  appDefinition: AppDefinition;
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div>
      <Header3 className={classes.sectionHead}>Distribution settings</Header3>
      <div className={classes.subheader}>Manage how your app can be installed and used by consumers.</div>
      <BenchlingAppSettingsRow>
        <SettingsField
          label="Audience"
          name="audience"
          value={appDefinition.audience === Audience.PUBLIC ? 'Public' : 'Private'}
          isBeingCreated={false}
          onChange={() => {}}
          isWritable={false}
        />
      </BenchlingAppSettingsRow>
    </div>
  );
}

interface BenchlingAppSettingsRowProps {
  className?: string;
  children: ReactNode;
}

export function BenchlingAppSettingsRow(props: BenchlingAppSettingsRowProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classNames(props.className, classes.settingsRow)}>
      {React.Children.map(props.children, (child) => (
        <div className={classes.settingsColumn}>
          <div className={classNames(classes.settingsField, 'u-base-text')}>{child}</div>
        </div>
      ))}
    </div>
  );
}

interface OverviewSectionProps {
  children: ReactNode;
}

export function OverviewSection(props: OverviewSectionProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div>
      {React.Children.map(props.children, (child) => (
        <div className={classes.overviewSection}>{child}</div>
      ))}
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  settingsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    width: '100%',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  settingsColumn: {
    flex: '1 1 200px',
    minWidth: 200,
  },
  settingsField: {
    width: '100%',
    marginBottom: 0,
  },
  sectionHead: {
    marginTop: theme.spacing.md,
  },
  savingContainer: {
    position: 'relative',
  },
  subheader: {
    marginBottom: theme.spacing.xs,
  },
  overviewSection: {
    marginBottom: theme.spacing.lg,
  },
}));
